"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { Attachment as V1Attachment, EntityId, UploadScope } from "@theta/api-client";
import {
  kindForFile,
  moveItem,
  type Attachment,
  type AttachmentKind,
} from "@/lib/postRaw";
import { UPLOAD_PROCESSING_FAILED, uploadFile } from "@/lib/api/uploads";
import { uploadVideo, type VideoUploadHandle } from "@/lib/api/videoUpload";
import { rejectionForFile } from "@/lib/upload-limits";
import { useUploadLimits } from "@/lib/hooks/usePlatformMeta";

/** v1 calls audio its own kind; the tray draws it the same as any other file. */
function trayKind(kind: V1Attachment["kind"]): AttachmentKind {
  return kind === "audio" ? "file" : kind;
}

/**
 * An existing post's or comment's attachments, ready to edit.
 *
 * This replaces `parsePostBody(post.raw).attachments` — the editor is handed
 * the rows the document already carries instead of re-deriving them from
 * markdown tokens that the server had itself derived them from.
 */
function fromDocument(attachments: V1Attachment[]): Attachment[] {
  return attachments.map((a) => ({
    id: `upload:${a.uploadId}`,
    uploadId: a.uploadId,
    kind: trayKind(a.kind),
    name: a.filename,
    // CARRIED, NOT ASSUMED (OSO-490). An editor opened on a post whose video
    // is still encoding — or whose transcode failed — gets the row in the tray
    // with its real state, so the badge is honest and so that saving an
    // unrelated edit does not drop the attachment on the floor.
    status: a.status,
    url: a.url,
    renditions: a.renditions,
  }));
}

/**
 * The attachments behind any composer or editor: upload, remove, reorder.
 *
 * One implementation for posts, comments, replies and event comments — a
 * comment should not gain or lose the ability to carry a photo depending on
 * which box it was typed into.
 *
 * `scope` IS REQUIRED because §1.11 authorises an upload's scope at the moment
 * it is uploaded, so the file has an ACL before anything references it. There
 * is no sensible default for "who may read this".
 *
 * Pass a document's `attachments` to edit what it already has; pass nothing to
 * compose from scratch.
 */
export function useAttachments(scope: UploadScope, initial?: V1Attachment[]) {
  const [attachments, setAttachments] = useState<Attachment[]>(() =>
    initial ? fromDocument(initial) : []
  );
  const [error, setError] = useState<string | null>(null);
  // Uploads run one at a time so the tray order matches the order files were
  // picked or pasted, not whichever request happened to finish first.
  const queue = useRef<Promise<void>>(Promise.resolve());
  const objectUrls = useRef<string[]>([]);
  /**
   * In-flight Bunny video uploads, keyed by the tray row id, so removing a row
   * mid-upload aborts the TUS transfer rather than leaving it streaming to the
   * CDN in the background (OSO-645).
   */
  const videoUploads = useRef<Map<string, VideoUploadHandle>>(new Map());
  /**
   * Read at call time so a caller may compute the scope from state — the DM
   * composer's conversation id only exists once the thread does — without
   * re-creating `addFiles` on every render and restarting the upload queue.
   */
  const scopeRef = useRef(scope);
  useEffect(() => {
    scopeRef.current = scope;
  }, [scope]);
  /**
   * The server's own size limits, read from `GET /meta` and held in a ref for
   * the same reason `scope` is: they arrive after the first render and must
   * not restart the upload queue when they do.
   */
  const limits = useUploadLimits();
  const limitsRef = useRef(limits);
  useEffect(() => {
    limitsRef.current = limits;
  }, [limits]);

  useEffect(() => {
    const urls = objectUrls.current;
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const upload = useCallback(async (file: File) => {
    const id = crypto.randomUUID();
    const kind = kindForFile(file);
    const preview =
      kind === "file"
        ? undefined
        : (() => {
            const url = URL.createObjectURL(file);
            objectUrls.current.push(url);
            return url;
          })();

    setAttachments((prev) => [
      ...prev,
      { id, kind, name: file.name, status: "ready", url: "", preview, uploading: true },
    ]);

    try {
      /**
       * ONE STEP. THE 201 IS THE END OF IT — OSO-490 reversed D1.
       *
       * `POST /uploads` answers `ready` for everything but a video and
       * `processing` for a video, and either way the file is now ATTACHABLE:
       * `contentRepo.findUploads` admits both, and a post carrying a
       * `processing` one is held back by `published_at` until the worker
       * settles it. So `uploading` goes false here, "Slå op" enables, and the
       * member is not made to watch a transcode they cannot hurry.
       *
       * This is where `awaitUploadReady` used to sit, polling every 2 s for up
       * to five minutes. Removing it is the whole point of the slice; the
       * server's `theta.reap-stuck-uploads` sweep owns the never-finishes case
       * now, and it reaches the author whether or not this tab is still open.
       *
       * `failed` IN THE 201 IS STILL A REFUSAL. The transcode can only have
       * failed this fast if it failed on the way in, there is nothing to
       * attach, and the server would refuse the id anyway. Throwing here puts
       * it through the same catch as any other rejected file: the tray row
       * goes away and the member is told why.
       *
       * A VIDEO IN BUNNY MODE TAKES THE OTHER ROAD (OSO-645). The bytes stream
       * straight to the CDN over TUS, reporting progress, and what comes back is
       * the same `processing` `Upload` the local path yields — so everything
       * below this is identical. `videoHost` comes from `/meta`, so the server
       * owns the switch and this client ships no hard-coded choice.
       */
      let result: Awaited<ReturnType<typeof uploadFile>>;
      if (kind === "video" && limitsRef.current?.videoHost === "bunny") {
        const handle = uploadVideo(file, {
          scope: scopeRef.current,
          onProgress: (fraction) =>
            setAttachments((prev) =>
              prev.map((a) => (a.id === id ? { ...a, progress: fraction } : a)),
            ),
        });
        videoUploads.current.set(id, handle);
        try {
          result = await handle.done;
        } finally {
          videoUploads.current.delete(id);
        }
      } else {
        result = await uploadFile(file, { scope: scopeRef.current });
      }
      if (result.status === "failed") throw new Error(UPLOAD_PROCESSING_FAILED);

      setAttachments((prev) =>
        prev.map((a) =>
          a.id === id
            ? {
                ...a,
                uploading: false,
                status: result.status,
                name: result.filename,
                url: result.url,
                renditions: result.renditions,
                // The row id — this is what the write path sends (§1.12).
                uploadId: result.id,
              }
            : a
        )
      );
    } catch (err) {
      setAttachments((prev) => prev.filter((a) => a.id !== id));
      // A DELIBERATE REMOVAL DURING UPLOAD IS NOT AN ERROR. `remove` aborts the
      // TUS transfer, which rejects `done` with this sentinel; showing "could
      // not upload" for a file the member just chose to drop would be a lie.
      if (err instanceof Error && err.message === "afbrudt") return;
      setError(
        (err instanceof Error && err.message) ||
          (kind === "file"
            ? "Filen kunne ikke uploades. Prøv igen."
            : "Billedet kunne ikke uploades. Prøv igen.")
      );
    }
  }, []);

  const addFiles = useCallback(
    (files: FileList | File[] | null) => {
      if (!files) return;
      setError(null);
      for (const file of Array.from(files)) {
        /**
         * REFUSED BEFORE A BYTE LEAVES, when the file is plainly too big.
         * The alternative is what OSO-437 reported: a 24 MB clip climbing a
         * phone's uplink for half a minute to earn a bare `HTTP 500`. The
         * size is in hand and `/meta` has already said what the server takes,
         * so there is nothing to learn by sending it. A file we have no
         * limits for is still sent — the server remains the authority, and
         * this is only ever the faster half of the same answer.
         */
        const rejection = rejectionForFile(file, limitsRef.current);
        if (rejection) {
          setError(rejection);
          continue;
        }
        queue.current = queue.current.then(() => upload(file));
      }
    },
    [upload]
  );

  const remove = useCallback((id: string) => {
    // Abort an in-flight Bunny upload so its bytes stop climbing the uplink the
    // moment the member drops the row (OSO-645); a local upload has no handle.
    videoUploads.current.get(id)?.abort();
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const move = useCallback(
    (from: number, to: number) => setAttachments((prev) => moveItem(prev, from, to)),
    []
  );

  const reset = useCallback(() => {
    setAttachments([]);
    setError(null);
  }, []);

  /**
   * PUT A CLEARED TRAY BACK — for a write that was refused (OSO-563).
   *
   * A composer empties the tray the moment it sends, so the picture appears
   * once (on the comment) rather than twice. That is right until the request
   * fails: the text comes back, the attachments do not, and pressing send
   * again quietly posts without them. The rows handed here are the ones the
   * composer captured at `mutate()` — already uploaded, so their `uploadId`
   * and `url` are still good and nothing is re-uploaded.
   */
  const restore = useCallback((rows: Attachment[]) => {
    if (rows.length > 0) setAttachments(rows);
  }, []);

  return {
    attachments,
    isUploading: attachments.some((a) => a.uploading),
    /** Anything finished uploading — i.e. anything that will reach the request. */
    hasContent: attachments.some((a) => !a.uploading),
    /**
     * The rows a create/update sends, in display order. The id is branded —
     * it came off `Upload.id`, so a group id cannot be passed here by
     * accident and no cast stands between the upload and the write.
     *
     * AN ID IS ALL IT SENDS, AND THAT IS THE FIX, NOT THE OMISSION (OSO-437).
     * The wire has an optional `kind`, and the server used to default the
     * missing one to `"image"` — so every video posted from here was stored as
     * an image row and `videoAttachments()` never drew it. The tray cannot
     * answer that question anyway: it has three kinds and no `audio`, and
     * `File.type` is the operating system's guess. The server classifies the
     * upload row it already fetched (`attachmentKindFor`), so sending a `kind`
     * from here would be a claim nobody reads.
     */
    uploadIds: attachments
      .filter((a) => a.uploadId != null)
      .map((a) => ({ uploadId: a.uploadId as EntityId<"upload"> })),
    error,
    addFiles,
    remove,
    move,
    reset,
    restore,
  };
}
